import type { FC, ReactNode } from "react";
import { Icon } from "../primitives";
import type { FinancialsMetrics } from "./hooks/useFinancialsMetrics";
import {
	buildFinancialMetricStripItems,
	type FinancialMetricStripItem,
} from "./utils/financialMetricValues";

const CompactFinancialMetric = ({
	id,
	label,
	value,
	showSeparator,
}: FinancialMetricStripItem & { showSeparator: boolean }) => (
	<>
		{showSeparator && <span className="shrink-0 text-tertiary">·</span>}
		<span data-testid={`metric-strip-${id}`} className="flex items-center gap-1.5 shrink-0">
			<span className="text-[10px] uppercase tracking-[0.6px] text-tertiary font-semibold">
				{label}
			</span>
			<span className="text-xs font-bold text-primary">{value}</span>
		</span>
	</>
);

export type FinancialMetricsCompactStripProps = {
	metrics: FinancialsMetrics;
	isExpanded: boolean;
	onToggle: () => void;
	children?: ReactNode;
};

export const FinancialMetricsCompactStrip: FC<FinancialMetricsCompactStripProps> = ({
	metrics,
	isExpanded,
	onToggle,
	children,
}) => {
	const stripItems = isExpanded ? null : buildFinancialMetricStripItems(metrics);

	return (
		<div className="mb-2 overflow-hidden rounded-lg border border-bc-default bg-surface-base shadow-sm">
			<button
				type="button"
				data-testid="financial-metrics-compact-strip"
				aria-expanded={isExpanded}
				aria-label={isExpanded ? "Collapse financial metrics" : "Expand financial metrics"}
				onClick={onToggle}
				className="w-full flex items-center gap-2 overflow-x-auto whitespace-nowrap px-3 py-1.5 text-left tabular-nums"
			>
				{stripItems && (
					<>
						<span className="shrink-0 text-sm font-bold text-secondary">YTD</span>
						{stripItems.ytd.map((item, index) => (
							<CompactFinancialMetric key={item.id} {...item} showSeparator={index > 0} />
						))}
						<span className="shrink-0 pb-[2px] text-xl text-tertiary">|</span>
						<span className="shrink-0 text-sm font-bold text-secondary">FYE</span>
						{stripItems.fye.map((item, index) => (
							<CompactFinancialMetric key={item.id} {...item} showSeparator={index > 0} />
						))}
					</>
				)}
				<span className="ml-auto shrink-0 flex pl-2">
					<Icon type={isExpanded ? "chevron-up" : "chevron-down"} size="mini" color="secondary" />
				</span>
			</button>
			{isExpanded && (
				<div className="flex gap-4 overflow-x-auto px-3 pb-2 [&>*]:shrink-0 [&>*]:w-[185px] scrollbar-hide">
					{children}
				</div>
			)}
		</div>
	);
};
